import {
  Button,
  Input,
  Paginate,
  Table,
  TableData,
  TableRow,
  ToastMessage
} from '@cokitchen/cokitchen-components';
import { AddIcon } from 'cokitchen-icons';
import React, { useState, useEffect, useCallback } from 'react';
import { ReactComponent as EditIcon } from 'assets/icons/edit-icon.svg';
import { ReactComponent as ArchiveIcon } from 'assets/icons/archive-icon.svg';
import './styles.scss';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AddVendorModal from './add-configuration-modal';
import BulkActions from './bulk-vendor-actions';
import ArchiveModal from 'components/modals/archive-manage-meals-modal';
import { ConversionListFormType, ConversionListType } from 'models/pricing.model';
import { apiService } from 'services/base.service';
import { debounce, handleApiError, sortConvertionItem } from 'utils/functions';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import usePricingContext from 'contexts/pricing.context';
import useRoleContext from 'contexts/role.context';
import { InventoryModule } from 'models/permission.model';
import { EDIT_PERMISSION_ACTIONS } from 'utils/authorization';

const Configuration = () => {
  const navigate = useNavigate();
  const { can } = useRoleContext();
  const { setStep, setConversionListFormData, setSelectedConversionItem } = usePricingContext();
  const [selectedItems, setSelectedItems] = useState<ConversionListType[]>([]);
  const [openedItems, setOpenedItems] = useState<string[]>([]);
  const [singleSelectedMix, setSingleSelectedMix] = useState<ConversionListType | null>(null);
  const [mixesArray, setMixesArray] = useState<ConversionListType[]>([]);
  const [ShowAddMixModal, setShowAddMixModal] = useState(false);
  const [isFetchingMeals, setIsFetchingMeals] = useState(false);
  const [lastPage, setLastPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');

  const toggleAddMix = () => {
    setShowAddMixModal(!ShowAddMixModal);
  };

  const isAllSelected = () => {
    return mixesArray?.length > 0 && mixesArray?.length === selectedItems.length;
  };
  const selectMix = (id: string) => {
    const mix = mixesArray.find((mix) => mix.ingredient.id === id);
    if (mix) {
      if (selectedItems.includes(mix)) {
        setSelectedItems(selectedItems.filter((mix) => mix.ingredient.id !== id));
      } else {
        setSelectedItems([...selectedItems, mix]);
      }
    }
  };

  const toggleItemRow = (id: string) => {
    if (openedItems.includes(id)) {
      setOpenedItems(openedItems?.filter((_) => _ !== id));
    } else {
      setOpenedItems([...openedItems, id]);
    }
  };

  const [showBulkActions, setShowBulkActions] = useState<boolean>(true);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const canEditItemConversion = can(InventoryModule.ItemConversion, EDIT_PERMISSION_ACTIONS);
  const [isArchiving, setIsArchiving] = useState(false);

  const handleArchive = () => {
    if (!canEditItemConversion) return;
    setShowArchiveModal(true);
  };

  const handleArchiveConfirm = async () => {
    setIsArchiving(true);
    try {
      const itemsToArchive =
        selectedItems.length > 0 ? selectedItems : singleSelectedMix ? [singleSelectedMix] : [];

      if (itemsToArchive.length === 0) return;

      const archivePromises = itemsToArchive.map((item) =>
        apiService.put(`ingredient-mix/${item.id}/archive`, {
          version: 'v2'
        })
      );

      await Promise.all(archivePromises);

      // Clean up state
      setSingleSelectedMix(null);
      setSelectedItems([]);
      setShowArchiveModal(false);

      await fetchIngredientMixes(search, '', currentPage);
      toast(<ToastMessage message='Meal(s) archived successfully' type='success' />);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsArchiving(false);
    }
  };

  const toggleAll = () => {
    if (!canEditItemConversion) return;
    if (isAllSelected()) {
      setSelectedItems([]);
    } else {
      setSelectedItems(mixesArray);
    }
  };

  const handleEdit = (item?: ConversionListType) => {
    if (!canEditItemConversion) return;
    const items: ConversionListFormType[] = item
      ? [sortConvertionItem(item)]
      : selectedItems?.map((_) => sortConvertionItem(_));
    const itemsSelected = {
      ...items[0],
      itemIndex: 0
    };
    navigate(`${MAIN_STORE_ROUTE_KEYS.EDIT_MEALS_CONFIGURATION}`, {
      state: items
    });
    setConversionListFormData(items);
    setSelectedConversionItem(itemsSelected);
    setStep(2);
  };

  useEffect(() => {
    if (selectedItems.length > 1) {
      setShowBulkActions(true);
    } else {
      setShowBulkActions(false);
    }
  }, [selectedItems]);

  const fetchIngredientMixes = async (
    searchString: string,
    categoryString: string,
    current_page?: number
  ) => {
    try {
      setIsFetchingMeals(true);
      const response = await apiService.get(`ingredient-mix`, {
        version: 'v2',
        params: {
          page: current_page || 1,
          name: searchString,
          type: 'meal'
        }
      });
      if (response) {
        setMixesArray(response.data.data);
        setLastPage(Math.ceil((response.data.pagination.total || 1) / 10));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsFetchingMeals(false);
    }
  };

  const debounceFetchMixes = useCallback(debounce(fetchIngredientMixes, 1000), []);

  useEffect(() => {
    setIsFetchingMeals(true);
    clearMixesList();
    debounceFetchMixes(search, '', currentPage);
  }, [search, '', currentPage]);

  const clearMixesList = () => {
    setMixesArray([]);
    setSelectedItems([]);
  };

  return (
    <div className='px-2 md:px-6 mt-2 mb-10 vendors'>
      <div className='flex justify-between items-center mb-8'>
        <div>
          <h4 className='text-[#2E3031] text-2xl font-semibold'>Meal Conversion list</h4>
          <p className='text-[#6F7376] text-sm'>
            This is the ingredient breakdown for meal items that need to be prepped
          </p>
        </div>
        {canEditItemConversion && (
          <Button
            onClick={toggleAddMix}
            variant='block'
            color='primary'
            className='w-[167px] text-white'
            small>
            <AddIcon className='w-[18px] h-[18px] text-white' />
            Add Meal
          </Button>
        )}
      </div>
      <div className='flex justify-between items-center mb-8'>
        <div className='flex gap-3 items-center'>
          <Input
            type='search'
            placeholder='Search meals'
            className='w-[300px] search-input'
            inputClass='h-[36px]'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Paginate
          pageCount={lastPage}
          pageRangeDisplayed={2}
          onPageChange={(e) => {
            clearMixesList();
            setCurrentPage(e.selected + 1);
          }}
        />
      </div>
      <Table
        headers={[]}
        isAllSelected={isAllSelected()}
        onToggleAll={toggleAll}
        loading={isFetchingMeals}>
        {mixesArray.map((mix) => {
          const isOpened = openedItems?.includes(mix.ingredient.id);
          return (
            <>
              <TableRow
                key={mix.id}
                // canSelect
                className={`table_row transition-colors ease-in-out duration-500  inactive_row ${
                  isOpened ? 'bg-[#F4F5F5]' : 'bg-white'
                }`}>
                <TableData className='w-0'>
                  <div
                    // onClick={() => navigate(vendor.ingredient.id.toString())}
                    onClick={() => toggleItemRow(mix.ingredient.id)}
                    className='flex justify-start items-center gap-3 cursor-pointer w-full'>
                    <Input
                      onClick={(e) => e.stopPropagation()}
                      type='checkbox'
                      className='mr-2'
                      checkColor='red'
                      checked={selectedItems.includes(mix)}
                      disabled={!canEditItemConversion}
                      onChange={() => {
                        if (!canEditItemConversion) return;
                        selectMix(mix.ingredient.id);
                      }}
                    />
                    <span className='text-base font-semibold'>{mix.ingredient.name}</span>
                    <span className='text-sm text-gray-12 bg-gray-13 px-2 py-0.5 rounded-full'>
                      {mix.no_of_portions} {mix.portion_type}
                    </span>
                  </div>
                </TableData>

                <TableData className='w-0'>
                  <div
                    className='opacity-0 cursor-pointer'
                    onClick={() => toggleItemRow(mix.ingredient.id)}>
                    Quantity
                  </div>
                </TableData>
                <TableData className='w-0'>
                  {canEditItemConversion ? (
                    <div className='flex gap-3 w-[52px]'>
                      <button
                        onClick={() => handleEdit(mix)}
                        className='flex justify-center items-center gap-1 bg-white px-3 py-1 rounded-full'>
                        <EditIcon className='w-[18px] h-[18px]' />
                        <span className='text-gray-12 font-medium'>Edit</span>
                      </button>

                      <button
                        onClick={() => {
                          setSingleSelectedMix(mix);
                          setShowArchiveModal(true);
                        }}
                        className='flex justify-center items-center gap-1 bg-white px-3 py-1 rounded-full'>
                        <ArchiveIcon className='w-[18px] h-[18px]' />
                        <span className='text-[#E60A2B] font-medium'>Archive</span>
                      </button>
                    </div>
                  ) : (
                    ''
                  )}
                </TableData>
              </TableRow>
              {openedItems?.includes?.(mix.ingredient.id) ? (
                <>
                  <TableRow key={mix.id} className='table_row'>
                    <TableData className=''>
                      <span>Ingredients</span>
                    </TableData>

                    <TableData className=''>
                      <span>Quantity</span>
                    </TableData>
                    <TableData>
                      <span></span>
                    </TableData>
                  </TableRow>

                  {mix.items.map((item) => (
                    <TableRow key={item.id} className='table_row '>
                      <TableData className='py-0'>
                        <span>{item.ingredient.name}</span>
                      </TableData>

                      <TableData className='py-0'>
                        <span>
                          {item.quantity} {item.ingredient.unit_type}
                        </span>
                      </TableData>
                      <TableData>
                        <span></span>
                      </TableData>
                    </TableRow>
                  ))}
                </>
              ) : null}

              <tr className='h-11' />
            </>
          );
        })}
      </Table>
      {ShowAddMixModal && <AddVendorModal show={ShowAddMixModal} onClose={toggleAddMix} />}
      {showArchiveModal && canEditItemConversion && (
        <ArchiveModal
          show
          onClose={() => setShowArchiveModal(false)}
          singleSelectedVendor={singleSelectedMix}
          selectedVendor={selectedItems}
          onConfirm={handleArchiveConfirm}
          loading={isArchiving}
        />
      )}
      {canEditItemConversion && selectedItems.length !== 0 && (
        <BulkActions
          item={selectedItems}
          deleteFunc={handleArchive}
          showBulkActions={showBulkActions}
          setShowBulkActions={setShowBulkActions}
          setSelectedItems={setSelectedItems}
        />
      )}
    </div>
  );
};

export default Configuration;
